/**
 * Orquestra o dialog de envio WhatsApp já existente nesta branch
 * (SendWhatsAppMessageDialog / FluxZap). Não recria templates internos.
 *
 * Audiência ecommerce: se o CPF tiver cliente cadastrado (Person) nesta org,
 * o destino FluxZap é Person/{id} — não Patient.
 * @module useEcommerceSendMessage
 */

import { useCallback, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import type { Patient } from "fhir/r5";
import {
  SendWhatsAppMessageDialog,
  useMessagingTemplatesForSend,
  type MessagingPatient,
} from "src-next/modules/crm-clinical/messaging";
import {
  resolveEcommerceTaskPatientReference,
  resolveEcommerceWhatsAppRecipients,
  type EcommerceWhatsAppTarget,
} from "src-next/modules/ecommerce";

interface UseEcommerceSendMessageParams {
  organizationId?: string;
  practitionerId?: string;
  hasActiveMetaEndpoint?: boolean;
  /** Persons da loja — prioridade de destino no disparo. */
  personByCpf?: Map<string, fhir5.Person>;
}

/**
 * Abre o dialog de envio com destinatários da tela ecommerce.
 */
export function useEcommerceSendMessage({
  organizationId,
  practitionerId,
  hasActiveMetaEndpoint,
  personByCpf,
}: UseEcommerceSendMessageParams) {
  const [open, setOpen] = useState(false);
  const [messagingPatients, setMessagingPatients] = useState<
    MessagingPatient[]
  >([]);
  const [taskPatientReference, setTaskPatientReference] = useState<
    string | null
  >(null);
  const onSentRef = useRef<(() => void) | undefined>();
  const personByCpfRef = useRef(personByCpf);
  personByCpfRef.current = personByCpf;

  const { templates } = useMessagingTemplatesForSend({ organizationId });

  const appointment = useMemo(
    () => ({
      id: "ecommerce-ad-hoc",
      startAtIso: dayjs().toISOString(),
    }),
    [],
  );

  const openSend = useCallback(
    (
      input: Patient[] | EcommerceWhatsAppTarget[],
      options?: { onSent?: () => void },
    ) => {
      const recipients = resolveEcommerceWhatsAppRecipients(
        input,
        personByCpfRef.current ?? new Map(),
      );
      if (recipients.length === 0) return;
      onSentRef.current = options?.onSent;
      setMessagingPatients(recipients);
      setTaskPatientReference(
        resolveEcommerceTaskPatientReference(input, recipients.length),
      );
      setOpen(true);
    },
    [],
  );

  const closeSend = useCallback(() => {
    setOpen(false);
    setMessagingPatients([]);
    setTaskPatientReference(null);
    onSentRef.current = undefined;
  }, []);

  const handleMessageSent = useCallback(() => {
    onSentRef.current?.();
    closeSend();
  }, [closeSend]);

  const isSingle = messagingPatients.length === 1;

  const dialog = (
    <SendWhatsAppMessageDialog
      open={open}
      onClose={closeSend}
      appointment={appointment}
      organizationId={organizationId}
      practitionerId={practitionerId}
      templates={templates}
      patient={isSingle ? messagingPatients[0] : null}
      initialSelectedPatients={isSingle ? undefined : messagingPatients}
      staticPatientListForSelection={isSingle ? undefined : messagingPatients}
      taskPatientReference={taskPatientReference}
      hasActiveMetaEndpoint={hasActiveMetaEndpoint}
      onMessageSent={handleMessageSent}
    />
  );

  return { openSend, closeSend, dialog };
}
